/**
 * Shows an automation's result on the board as soon as the worker reports it:
 * the rows it wrote and their comment threads are re-read in place, and the
 * dialog's counts and history follow. Mounted once per open board.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { refreshWorkspaceBrief } from '@/entities/brief';
import {
  boardTypeOf,
  refreshTrackerItems,
  TRACKER_QUERY_KEYS,
  type Tracker,
} from '@/entities/tracker';
import {
  TRACKER_AUTOMATION_QUERY_KEYS,
  type TrackerAutomationList,
} from '@/entities/tracker-automation';
import { TRACKER_COMMENT_QUERY_KEYS } from '@/entities/tracker-comment';
import { useCurrentWorkspaceId } from '@/entities/workspace';

import { useTrackerEvents, type AutomationRunEvent } from './trackerEventStream';

/**
 * A run-now over a board reports one event per row, to every viewer at once.
 * The first event opens a window and everything that lands in it is read back
 * together, so a viewer refetches once per burst rather than once per row.
 */
export const LIVE_RESULTS_FLUSH_MS = 300;

interface PendingResults {
  /** Rows a rule changed, which are the ones to read back. */
  applied: Set<string>;
  /**
   * Rows a rule that may comment ran on. The event does not say whether it did, and a
   * comment or an agent's reply changes nothing else, so each of these threads is re-read.
   */
  ranOn: Set<string>;
  /** A rule added, moved or archived a row, so the list itself is stale. */
  rowsChanged: boolean;
  timer: ReturnType<typeof setTimeout>;
}

export function useAutomationLiveResults(tracker: Pick<Tracker, 'id' | 'itemSource'>): void {
  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId() ?? '';
  const pending = useRef<PendingResults | null>(null);
  const board = useRef({ tracker, workspaceId });
  board.current = { tracker, workspaceId };

  useEffect(
    () => () => {
      if (pending.current) clearTimeout(pending.current.timer);
      pending.current = null;
    },
    [tracker.id],
  );

  const flush = () => {
    const batch = pending.current;
    pending.current = null;
    if (!batch) return;
    const { tracker: current, workspaceId: ws } = board.current;
    void queryClient.invalidateQueries({
      queryKey: TRACKER_AUTOMATION_QUERY_KEYS.runsOf(current.id),
    });
    void queryClient.invalidateQueries({
      queryKey: TRACKER_AUTOMATION_QUERY_KEYS.list(current.id),
    });
    if (batch.ranOn.size > 0) {
      void queryClient.invalidateQueries({
        queryKey: TRACKER_COMMENT_QUERY_KEYS.summary(current.id),
      });
      for (const rowRef of batch.ranOn) {
        void queryClient.invalidateQueries({
          queryKey: TRACKER_COMMENT_QUERY_KEYS.list(current.id, rowRef),
        });
      }
    }
    if (!ws) return;
    if (batch.rowsChanged && boardTypeOf(current.itemSource) === 'free_form') {
      void queryClient.invalidateQueries({ queryKey: TRACKER_QUERY_KEYS.items(ws, current.id) });
      return;
    }
    if (batch.applied.size === 0) return;
    if (current.itemSource === 'briefs') {
      for (const briefId of batch.applied) void refreshWorkspaceBrief(queryClient, ws, briefId);
    } else if (current.itemSource === 'projects' || current.itemSource === 'assets') {
      // Record rows are read in pages, filtered and sorted; refetching them is
      // the one way to put a row back where its new values sort it.
      void queryClient.invalidateQueries({
        queryKey: TRACKER_QUERY_KEYS.allRows(ws, current.id),
      });
    } else {
      void refreshTrackerItems(queryClient, ws, current.id, batch.applied);
    }
  };

  /**
   * Only comment and agent actions post to a thread. A rule missing from the cached list
   * (not loaded yet, or added since) is assumed to, so no comment is ever missed.
   */
  const mayComment = (automationId: string) => {
    const rule = queryClient
      .getQueryData<TrackerAutomationList>(TRACKER_AUTOMATION_QUERY_KEYS.list(tracker.id))
      ?.items.find((r) => r.id === automationId);
    return !rule || rule.actions.some((a) => a.type === 'comment' || a.type === 'run_agent');
  };

  useTrackerEvents(tracker.id, (event: AutomationRunEvent) => {
    pending.current ??= {
      applied: new Set(),
      ranOn: new Set(),
      rowsChanged: false,
      timer: setTimeout(flush, LIVE_RESULTS_FLUSH_MS),
    };
    if (event.rowsChanged) pending.current.rowsChanged = true;
    if (event.runs.some((run) => mayComment(run.automationId))) {
      pending.current.ranOn.add(event.rowRef);
    }
    if (event.runs.some((run) => run.outcome === 'applied')) {
      pending.current.applied.add(event.rowRef);
    }
  });
}
